import './move-shares.css'

/** Depths of access, as a sentence says what somebody can do: "Can edit it". */
export const ABLE = {
  read: 'read',
  comment: 'comment on',
  write: 'edit',
} as const
/** The same depths, as a sentence says what somebody does now: "edits it now". */
export const DOES = {
  read: 'reads',
  comment: 'comments on',
  write: 'edits',
} as const

export type AccessLine = {
  personId: string
  name: string
  /** What changes for them, in one line. */
  change: string
}

/**
 * People whose access an act changes, each group under what lets them in —
 * a shared folder, or standing in a space. A move that shares and a move that
 * changes owner name people the same way, so the two read alike. The caller
 * holds them in `.move-shares`, beside its own lede.
 */
export function AccessGroups({
  groups,
}: {
  groups: readonly { key: string; heading: string; people: AccessLine[] }[]
}) {
  return (
    <>
      {groups.map((group) => (
        <section key={group.key} className="move-shares__group">
          <h3 className="move-shares__folder">{group.heading}</h3>
          <ul className="move-shares__people">
            {group.people.map((p) => (
              <li key={p.personId} className="move-shares__person">
                <span>{p.name}</span>
                <span className="move-shares__change">{p.change}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

/** Gather lines into groups by a key, keeping the order each key first appears. */
export function groupLines<T>(
  rows: readonly T[],
  keyOf: (row: T) => { key: string; heading: string },
  line: (row: T) => AccessLine,
) {
  const groups = new Map<
    string,
    { key: string; heading: string; people: AccessLine[] }
  >()
  for (const row of rows) {
    const { key, heading } = keyOf(row)
    const group = groups.get(key) ?? { key, heading, people: [] }
    group.people.push(line(row))
    groups.set(key, group)
  }
  return [...groups.values()]
}
